import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { voice } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text, Title, SimpleGrid } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { TextInput, Textarea, Select, NumberInput, PasswordInput, SegmentedControl } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { StatusPill, type PillTone } from '../../../components/panel';
import { useBots, botName } from '../../../lib/bots';
import { formatRelative, formatDate } from '../../../lib/format';
import { Applications } from './Applications';

const MUTED = 'var(--rr-color-text-muted)';
const ERROR = 'var(--rr-color-error, #dc2626)';
const NO_BOT = '00000000-0000-0000-0000-000000000000';
const LANGUAGES = ['en', 'af', 'zu', 'xh', 'st'] as const;

const message = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

/**
 * Phone providers and numbers.
 *
 * A number reaches the assistant through the organisation's own phone
 * provider, connected one of two ways: we sign in to the provider, or the
 * provider sends calls to our address. The page shows whether each
 * connection is working in the provider's own words, and whether the
 * assistant is ready to answer each number.
 */
export function Numbers({ status }: { status: voice.VoiceStatus }) {
  const t = useT();
  const qc = useQueryClient();
  // The call engine reports on a connection every few seconds, so the list is asked again while the page is open.
  const providers = useQuery({ queryKey: ['voice-providers'], queryFn: () => voice.listProviders(), refetchInterval: 10_000 });
  const numbers = useQuery({ queryKey: ['voice-numbers'], queryFn: () => voice.listNumbers(), refetchInterval: 10_000 });
  const [provider, setProvider] = useState<voice.VoiceProvider | 'new' | null>(null);
  const [number, setNumber] = useState<voice.VoiceNumber | 'new' | null>(null);
  const providerRows = providers.data?.data ?? [];
  const numberRows = numbers.data?.data ?? [];
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['voice-providers'] });
    void qc.invalidateQueries({ queryKey: ['voice-numbers'] });
  };

  const removeProvider = useMutation({
    mutationFn: (p: voice.VoiceProvider) => voice.deleteProvider(p.id),
    onSuccess: () => { refresh(); notifySuccess(t('voice.providers.removed')); },
    onError: (e) => notifyError(message(e, t('common.unknownError')))
  });
  const removeNumber = useMutation({
    mutationFn: (n: voice.VoiceNumber) => voice.deleteNumber(n.id),
    onSuccess: () => { refresh(); notifySuccess(t('voice.numbers.removed')); },
    onError: (e) => notifyError(message(e, t('common.unknownError')))
  });
  const pause = useMutation({
    mutationFn: (n: voice.VoiceNumber) => voice.updateNumber(n.id, { paused: n.status === 'active' }),
    onSuccess: () => refresh(),
    onError: (e) => notifyError(message(e, t('common.unknownError')))
  });

  const providerTone = (p: voice.VoiceProvider): PillTone => (p.status === 'ok' ? 'active' : p.status === 'failing' ? 'rejected' : 'submitted');
  const assistantLine = (n: voice.VoiceNumber) =>
    n.status !== 'active' ? t('voice.numbers.assistant.paused')
      : n.assistant_status === 'registered' ? t('voice.numbers.assistant.ready')
        : n.assistant_status === 'failed' ? t('voice.numbers.assistant.failed')
          : t('voice.numbers.assistant.pending');

  return (
    <Stack gap="lg">
      <Card withBorder data-testid="voice-numbers">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start" wrap="wrap">
            <Stack gap="xs" style={{ minWidth: 0, flex: 1 }}>
              <Title order={4}>{t('voice.numbers.title')}</Title>
              <Text role="bodySmall" c={MUTED}>{t('voice.numbers.intro')}</Text>
            </Stack>
            <Button size="xs" variant={providerRows.length === 0 ? 'default' : 'filled'} disabled={providerRows.length === 0} onClick={() => setNumber('new')}>{t('voice.numbers.add')}</Button>
          </Group>
          {numbers.error && <Alert color="error" role="alert">{message(numbers.error, t('common.unknownError'))}</Alert>}
          {!numbers.isPending && numberRows.length === 0 && (
            <EmptyState title={t('voice.numbers.empty')} description={t(providerRows.length === 0 ? 'voice.numbers.emptyNoProvider' : 'voice.numbers.emptyHint')} />
          )}
          {numberRows.map((n) => (
            <div key={n.id} className="rr-voice__row" data-testid="voice-number">
              <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                <Group gap="xs" wrap="wrap">
                  <Text role="bodySmall" fw={650}>{n.number}</Text>
                  {n.label && <Text role="bodySmall" c={MUTED}>{n.label}</Text>}
                  {n.source === 'platform' && <StatusPill tone="neutral">{t('voice.numbers.fromUs')}</StatusPill>}
                  {n.source === 'whatsapp' && <StatusPill tone="neutral">{t('voice.numbers.whatsapp')}</StatusPill>}
                  <StatusPill tone={n.status === 'active' ? 'active' : n.status === 'suspended' ? 'rejected' : 'paused'}>{t(`voice.numbers.status.${n.status}`)}</StatusPill>
                </Group>
                <Text role="caption" c={n.status === 'active' && n.assistant_status === 'failed' ? ERROR : MUTED}>{assistantLine(n)}</Text>
                {n.status === 'active' && n.assistant_status === 'failed' && n.assistant_error && <Text role="caption" c={MUTED}>{n.assistant_error}</Text>}
                {n.status === 'suspended' && n.suspended_reason && <Text role="caption" c={ERROR}>{n.suspended_reason}</Text>}
                {n.source === 'whatsapp' && <Text role="caption" c={MUTED}>{t('voice.numbers.whatsappHint')}</Text>}
                {n.rental_overdue_at
                  ? <Text role="caption" c={ERROR}>{t('voice.numbers.rental.overdue')}</Text>
                  : n.next_rental_at ? <Text role="caption" c={MUTED}>{t('voice.numbers.rental.next', { when: formatDate(n.next_rental_at) })}</Text> : null}
              </Stack>
              <Group gap={4} wrap="nowrap">
                {n.status !== 'suspended' && (
                  <Button size="xs" variant="default" loading={pause.isPending && pause.variables?.id === n.id} onClick={() => pause.mutate(n)}>
                    {t(n.status === 'active' ? 'voice.numbers.pause' : 'voice.numbers.resume')}
                  </Button>
                )}
                <Button size="xs" variant="subtle" onClick={() => setNumber(n)}>{t('action.edit')}</Button>
                {/* A WhatsApp number is switched off where it was switched on, the Numbers page, so Meta drops the call button too. */}
                {n.source !== 'whatsapp' && <Button
                  size="xs" variant="subtle" color="error"
                  onClick={() => openConfirmModal({
                    title: t('voice.numbers.removeTitle'),
                    children: <Text role="bodySmall">{t('voice.numbers.removeBody', { number: n.number })}</Text>,
                    labels: { confirm: t('action.delete'), cancel: t('action.cancel') },
                    confirmProps: { color: 'red' },
                    onConfirm: () => removeNumber.mutate(n)
                  })}
                >{t('action.delete')}</Button>}
              </Group>
            </div>
          ))}
        </Stack>
      </Card>

      <Applications status={status} />

      <Card withBorder data-testid="voice-providers">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start" wrap="wrap">
            <Stack gap="xs" style={{ minWidth: 0, flex: 1 }}>
              <Title order={4}>{t('voice.providers.title')}</Title>
              <Text role="bodySmall" c={MUTED}>{t('voice.providers.intro')}</Text>
            </Stack>
            <Button size="xs" variant={providerRows.length === 0 ? 'filled' : 'default'} onClick={() => setProvider('new')}>{t('voice.providers.add')}</Button>
          </Group>
          {providers.error && <Alert color="error" role="alert">{message(providers.error, t('common.unknownError'))}</Alert>}
          {!providers.isPending && providerRows.length === 0 && (
            <EmptyState title={t('voice.providers.empty')} description={t('voice.providers.emptyHint')} />
          )}
          {providerRows.map((p) => (
            <div key={p.id} className="rr-voice__row" data-testid="voice-provider">
              <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                <Group gap="xs" wrap="wrap">
                  <Text role="bodySmall" fw={650}>{p.name}</Text>
                  <StatusPill tone={providerTone(p)}>{t(`voice.providers.status.${p.status}`)}</StatusPill>
                </Group>
                <Text role="caption" c={MUTED}>
                  {t(`voice.providers.modeLine.${p.mode}`, { host: p.host })}
                  {p.last_checked_at ? ` · ${t('voice.providers.checked', { when: formatRelative(p.last_checked_at) })}` : ''}
                </Text>
                {p.status === 'failing' && p.last_error && <Text role="caption" c={ERROR}>{p.last_error}</Text>}
              </Stack>
              <Group gap={4} wrap="nowrap">
                <Button size="xs" variant="subtle" onClick={() => setProvider(p)}>{t('action.edit')}</Button>
                <Button
                  size="xs" variant="subtle" color="error"
                  onClick={() => openConfirmModal({
                    title: t('voice.providers.removeTitle'),
                    children: <Text role="bodySmall">{t('voice.providers.removeBody', { name: p.name })}</Text>,
                    labels: { confirm: t('action.delete'), cancel: t('action.cancel') },
                    confirmProps: { color: 'red' },
                    onConfirm: () => removeProvider.mutate(p)
                  })}
                >{t('action.delete')}</Button>
              </Group>
            </div>
          ))}
        </Stack>
      </Card>

      <ProviderForm provider={provider} sipHost={status.sip_host ?? null} onClose={() => setProvider(null)} onSaved={refresh} />
      <NumberForm number={number} providers={providerRows} onClose={() => setNumber(null)} onSaved={refresh} />
    </Stack>
  );
}

function ProviderForm({ provider, sipHost, onClose, onSaved }: { provider: voice.VoiceProvider | 'new' | null; sipHost: string | null; onClose: () => void; onSaved: () => void }) {
  const t = useT();
  const existing = provider !== null && provider !== 'new' ? provider : null;
  const [name, setName] = useState('');
  const [mode, setMode] = useState<voice.VoiceProviderMode>('register');
  const [host, setHost] = useState('');
  const [port, setPort] = useState<number | ''>(5060);
  const [transport, setTransport] = useState<'udp' | 'tcp' | 'tls'>('udp');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [sources, setSources] = useState('');

  useEffect(() => {
    if (provider === null) return;
    setName(existing?.name ?? '');
    setMode(existing?.mode ?? 'register');
    setHost(existing?.host ?? '');
    setPort(existing?.port ?? 5060);
    setTransport(existing?.transport ?? 'udp');
    setUsername(existing?.username ?? '');
    setPassword('');
    setSources((existing?.allowed_sources ?? []).join('\n'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  const save = useMutation({
    mutationFn: () => {
      const input: voice.VoiceProviderInput = {
        name: name.trim(), mode, host: host.trim(), port: port === '' ? 5060 : port, transport,
        username: mode === 'register' ? username.trim() : undefined,
        password: mode === 'register' && password.length > 0 ? password : undefined,
        allowed_sources: sources.split(/[\n,]/).map((s) => s.trim()).filter((s) => s.length > 0)
      };
      return existing ? voice.updateProvider(existing.id, input) : voice.createProvider(input);
    },
    onSuccess: () => { onSaved(); onClose(); notifySuccess(t('voice.providers.saved')); },
    onError: (e) => notifyError(message(e, t('common.unknownError')))
  });

  const ready = name.trim().length > 0 && host.trim().length > 0
    && (mode === 'forward' || (username.trim().length > 0 && (password.length > 0 || existing?.has_password === true)));

  return (
    <Drawer opened={provider !== null} onClose={onClose} title={t(existing ? 'voice.providers.form.edit' : 'voice.providers.form.new')} position="right" size="lg">
      <Stack gap="md" data-testid="voice-provider-form">
        <TextInput label={t('voice.providers.form.name')} description={t('voice.providers.form.nameHint')} value={name} maxLength={120} onChange={(e) => setName(e.currentTarget.value)} />
        <Stack gap={6}>
          <Text role="bodySmall" fw={600}>{t('voice.providers.form.mode')}</Text>
          <SegmentedControl
            value={mode}
            onChange={(v) => setMode(v as voice.VoiceProviderMode)}
            data={[{ value: 'register', label: t('voice.providers.form.mode.register') }, { value: 'forward', label: t('voice.providers.form.mode.forward') }]}
          />
          <Text role="caption" c={MUTED}>{t(`voice.providers.form.modeHint.${mode}`)}</Text>
        </Stack>
        <TextInput label={t('voice.providers.form.host')} description={t('voice.providers.form.hostHint')} value={host} onChange={(e) => setHost(e.currentTarget.value)} />
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <NumberInput label={t('voice.providers.form.port')} description={t('voice.providers.form.portHint')} value={port} min={1} max={65535} onChange={(v) => setPort(typeof v === 'number' ? v : '')} />
          <Select
            label={t('voice.providers.form.transport')} description={t('voice.providers.form.transportHint')}
            value={transport} onChange={(e) => setTransport(e.currentTarget.value as 'udp' | 'tcp' | 'tls')}
            data={[{ value: 'udp', label: 'UDP' }, { value: 'tcp', label: 'TCP' }, { value: 'tls', label: 'TLS' }]}
          />
        </SimpleGrid>
        {mode === 'register' ? (
          <>
            <TextInput label={t('voice.providers.form.username')} description={t('voice.providers.form.usernameHint')} value={username} autoComplete="off" onChange={(e) => setUsername(e.currentTarget.value)} />
            <PasswordInput
              label={t('voice.providers.form.password')}
              description={t(existing?.has_password ? 'voice.providers.form.passwordKeep' : 'voice.providers.form.passwordHint')}
              value={password} autoComplete="new-password" onChange={(e) => setPassword(e.currentTarget.value)}
            />
          </>
        ) : (
          <>
            {sipHost && (
              <Alert color="info">
                <Stack gap={4}>
                  <Text role="bodySmall">{t('voice.providers.form.sendTo')}</Text>
                  <Text role="bodySmall" fw={650} style={{ fontFamily: 'var(--rr-font-mono, monospace)' }}>{`sip:${t('voice.providers.form.sendToNumber')}@${sipHost}`}</Text>
                </Stack>
              </Alert>
            )}
            <Textarea label={t('voice.providers.form.sources')} description={t('voice.providers.form.sourcesHint')} value={sources} minRows={2} onChange={(e) => setSources(e.currentTarget.value)} />
          </>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
          <Button loading={save.isPending} disabled={!ready} onClick={() => save.mutate()}>{t('action.save')}</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}

function NumberForm({ number, providers, onClose, onSaved }: { number: voice.VoiceNumber | 'new' | null; providers: voice.VoiceProvider[]; onClose: () => void; onSaved: () => void }) {
  const t = useT();
  const { bots } = useBots();
  const existing = number !== null && number !== 'new' ? number : null;
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [providerId, setProviderId] = useState<string | null>(null);
  const [botId, setBotId] = useState<string>(NO_BOT);
  const [greeting, setGreeting] = useState('');
  const [language, setLanguage] = useState('en');
  const [instructions, setInstructions] = useState('');
  const [answerWith, setAnswerWith] = useState<'assistant' | 'department'>('assistant');
  const [departmentId, setDepartmentId] = useState('');
  const groups = useQuery({ queryKey: ['voice-groups'], queryFn: () => voice.listGroups(), enabled: number !== null });
  const departments = (groups.data?.data ?? []).filter((g) => g.is_active);

  useEffect(() => {
    if (number === null) return;
    setValue(existing?.number ?? '');
    setLabel(existing?.label ?? '');
    setProviderId(existing?.provider_account_id ?? providers[0]?.id ?? null);
    setBotId(existing?.bot_id ?? NO_BOT);
    setGreeting(existing?.handling.greeting ?? t('voice.numbers.form.greetingDefault'));
    setLanguage(existing?.handling.language || 'en');
    setInstructions(existing?.handling.instructions ?? '');
    setAnswerWith(existing?.handling.answer_with ?? 'assistant');
    setDepartmentId(existing?.handling.department_id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [number]);

  const save = useMutation({
    mutationFn: () => {
      const first = answerWith === 'department' && departmentId ? 'department' as const : 'assistant' as const;
      const handling = { greeting: greeting.trim(), language, instructions: instructions.trim(), answer_with: first, department_id: first === 'department' ? departmentId : null };
      return existing
        ? voice.updateNumber(existing.id, { label: label.trim(), bot_id: botId, handling })
        : voice.createNumber({ number: value.trim(), label: label.trim() || undefined, provider_account_id: providerId ?? '', bot_id: botId === NO_BOT ? undefined : botId, handling });
    },
    onSuccess: () => { onSaved(); onClose(); notifySuccess(t('voice.numbers.saved')); },
    onError: (e) => notifyError(message(e, t('common.unknownError')))
  });

  return (
    <Drawer opened={number !== null} onClose={onClose} title={t(existing ? 'voice.numbers.form.edit' : 'voice.numbers.form.new')} position="right" size="lg">
      <Stack gap="md" data-testid="voice-number-form">
        <TextInput label={t('voice.numbers.form.number')} description={t('voice.numbers.form.numberHint')} value={value} disabled={existing !== null} placeholder="087 123 4567" onChange={(e) => setValue(e.currentTarget.value)} />
        <TextInput label={t('voice.numbers.form.label')} description={t('voice.numbers.form.labelHint')} value={label} maxLength={120} onChange={(e) => setLabel(e.currentTarget.value)} />
{existing?.source !== 'platform' && existing?.source !== 'whatsapp' && (
                <Select
            label={t('voice.numbers.form.provider')} description={t('voice.numbers.form.providerHint')}
            value={providerId ?? ''} disabled={existing !== null} onChange={(e) => setProviderId(e.currentTarget.value)}
            data={providers.map((p) => ({ value: p.id, label: p.name }))}
          />
        )}
        <Select
          label={t('voice.numbers.form.assistant')} description={t('voice.numbers.form.assistantHint')}
          value={botId} onChange={(e) => setBotId(e.currentTarget.value || NO_BOT)}
          data={[{ value: NO_BOT, label: t('voice.numbers.form.assistantDefault') }, ...bots.map((b) => ({ value: b.id, label: botName(b, t('settings.assistant.defaultName')) }))]}
        />
        {departments.length > 0 && (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Select
              label={t('voice.numbers.form.answerWith')} description={t(`voice.numbers.form.answerWithHint.${answerWith}`)}
              value={answerWith} onChange={(e) => setAnswerWith(e.currentTarget.value as 'assistant' | 'department')}
              data={[{ value: 'assistant', label: t('voice.numbers.form.answerWith.assistant') }, { value: 'department', label: t('voice.numbers.form.answerWith.department') }]}
            />
            {answerWith === 'department' && (
              <Select
                label={t('voice.numbers.form.department')} description={t('voice.numbers.form.departmentHint')}
                value={departmentId} onChange={(e) => setDepartmentId(e.currentTarget.value)}
                data={[{ value: '', label: t('voice.numbers.form.departmentChoose') }, ...departments.map((g) => ({ value: g.id, label: g.name }))]}
              />
            )}
          </SimpleGrid>
        )}
        <Textarea label={t('voice.numbers.form.greeting')} description={t('voice.numbers.form.greetingHint')} value={greeting} maxLength={300} minRows={2} onChange={(e) => setGreeting(e.currentTarget.value)} />
        <Select
          label={t('voice.numbers.form.language')} description={t('voice.numbers.form.languageHint')}
          value={language} onChange={(e) => setLanguage(e.currentTarget.value || 'en')}
          data={LANGUAGES.map((l) => ({ value: l, label: t(`voice.numbers.language.${l}`) }))}
        />
        <Textarea label={t('voice.numbers.form.instructions')} description={t('voice.numbers.form.instructionsHint')} value={instructions} maxLength={1500} minRows={3} onChange={(e) => setInstructions(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
          <Button loading={save.isPending} disabled={value.trim().length === 0 || (!existing && !providerId)} onClick={() => save.mutate()}>{t('action.save')}</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
